import queryString from 'query-string'
import React, { FC, ReactNode, createContext, useContext, useState } from 'react'

import { AuthApiService } from '@src/areas/auth/utils/api'
import { history } from '@src/configureHistory'
import { AppInitialStateResponse, GetTenantRegistrationFormValuesResponse } from '@src/models/api'
import { handleHttpError } from '@src/utils'

export interface IAuthContext {
    registrationValues: GetTenantRegistrationFormValuesResponse
    getRegistrationValues: () => Promise<any>
    registrationValuesLoading: boolean

    loggedIn: boolean
    signOut: (removeSessionStorageToken?: boolean) => Promise<void>
    signIn: (state: AppInitialStateResponse) => void

    appInitialState: AppInitialStateResponse
    authenticateInitially: () => Promise<AppInitialStateResponse>
}

export const AuthContext = createContext<IAuthContext>(null)

export const useAuthContext = () => {
    return useContext(AuthContext)
}

interface IAuthProviderProps {
    children: ReactNode
}

export const AuthProvider: FC<IAuthProviderProps> = ({ children }: IAuthProviderProps) => {
    const [isLoggedIn, setIsLoggedIn] = useState(false)

    const [registrationValues, setRegistrationValues] = useState<GetTenantRegistrationFormValuesResponse>(null)
    const [registrationValuesLoading, setRegistrationValuesLoading] = useState(true)
    const [appInitialState, setAppInitialState] = useState<AppInitialStateResponse>(null)

    const getRegistrationValues = async () => {
        if (!registrationValues) {
            setRegistrationValuesLoading(true)
            const { data } = await AuthApiService.getRegistrationFormValues()
            setRegistrationValues(data)
            setRegistrationValuesLoading(false)
            return data
        }

        return registrationValues
    }

    const signIn = (state: AppInitialStateResponse) => {
        setAppInitialState(state)
        setIsLoggedIn(true)
    }

    const signOut = async (removeSessionstorageToken = true) => {
        try {
            await AuthApiService.signOut(removeSessionstorageToken)
            setAppInitialState(null)
            setIsLoggedIn(false)
            FreshworksWidget('destroy', 'launcher')
        } catch (err) {
            handleHttpError(err)
            throw err
        }
    }

    const authenticateInitially = async () => {
        if (!appInitialState) {
            try {
                const queryToken = queryString.parse(history.location.search).token as string
                let initialState: AppInitialStateResponse

                if (queryToken) {
                    initialState = await AuthApiService.pwdlessTokenSignIn(queryToken)
                } else {
                    initialState = await AuthApiService.tenantTokenSignIn(true)
                }

                signIn(initialState)
                return initialState
            } catch {
                setIsLoggedIn(false)
            }
        }

        return appInitialState
    }

    return (
        <AuthContext.Provider
            value={{
                loggedIn: isLoggedIn,
                signOut,
                signIn,
                getRegistrationValues,
                registrationValues,
                registrationValuesLoading,
                authenticateInitially,
                appInitialState
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}
